import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import styles from './index.module.css';

import * as api from '../../api';
import * as Utils from './utils';
import * as logger from '../../logger';

import { AutocompleteAsyncInput, Suggestion } from '../AutocompleteAsyncInput';
import { ItemsList, Size } from '../ItemsPage/ItemsList';

import LocationOnIcon from '@material-ui/icons/LocationOn';
import SearchIcon from '@material-ui/icons/Search';

///////////////////////////////////////////////////
////// TYPES & CONST

const SEARCH_PLACEHOLDER = 'Search pharmacies...';
const EMPTY_STRING = '';
const MAX_PHARMACIES = 20;
const MINIMUM_CHARACTERS_TO_SEARCH = 3;

interface Props extends WithTranslation {
  initialPharmacies: api.Pharmacy.PrevisitPharmacy[];
  saveFieldValue: (newValue: string) => void;
  updateFieldValue: (newValue: string) => void;
  latitude?: number | null;
  longitude?: number | null;
}

interface State {
  pharmacies: api.Pharmacy.PrevisitPharmacy[];
  currentText: string;
}

///////////////////////////////////////////////////
////// COMPONENT

class PharmacyComponent extends React.Component<Props, State> {
  state: State = {
    pharmacies: this.props.initialPharmacies,
    currentText: EMPTY_STRING,
  };

  componentDidMount = async () => {
    const { saveFieldValue, updateFieldValue } = this.props;
    const { pharmacies } = this.state;
    if (!pharmacies.length) return;

    const pharmaciesWithStaticMapUrl = await Promise.all(
      pharmacies.map(async (pharmacy) => {
        const { longitude, latitude } = pharmacy.address;
        if (longitude && latitude && !pharmacy.staticMapUrl) {
          const staticMapUrl = await api.client.Maps.getStaticMapUrl({
            latitude: `${latitude}`,
            longitude: `${longitude}`,
          });
          return { ...pharmacy, staticMapUrl };
        }
        return pharmacy;
      }),
    );

    this.setState({ pharmacies: pharmaciesWithStaticMapUrl });
    saveFieldValue(JSON.stringify(pharmaciesWithStaticMapUrl));
    updateFieldValue(JSON.stringify(pharmaciesWithStaticMapUrl));
  };

  selectPharmacy = async (suggestion: api.Places.PlaceSuggestion) => {
    try {
      const placeDetails = await api.client.Places.findPlaceDetails(
        suggestion.placeId,
      );
      const staticMapUrl = await api.client.Maps.getStaticMapUrl({
        latitude: `${placeDetails.latitude}`,
        longitude: `${placeDetails.longitude}`,
      });
      const selectedPharmacy = {
        id: suggestion.placeId,
        name: suggestion.mainText,
        address: {
          address: placeDetails.addressOne,
          unitNumber: placeDetails.addressTwo,
          city: placeDetails.city,
          state: api.Address.stringToStateEnum(placeDetails.state),
          zip: placeDetails.zipCode,
          longitude: placeDetails.longitude,
          latitude: placeDetails.latitude,
        },
        kind: null,
        status: null,
        ncpdpId: null,
        phone: null,
        staticMapUrl,
      };
      const { saveFieldValue, updateFieldValue } = this.props;
      const { pharmacies } = this.state;
      const newPharmacies = [...pharmacies, selectedPharmacy];

      this.setState({ pharmacies: newPharmacies });
      saveFieldValue(JSON.stringify(newPharmacies));
      updateFieldValue(JSON.stringify(newPharmacies));
    } catch (err) {
      logger.error(err, '.selectPharmacy Failed selecting pharmacy');
    }
  };

  removePharmacy = (itemId: string) => {
    const { saveFieldValue, updateFieldValue } = this.props;
    const { pharmacies } = this.state;
    const updatedPharmacies = pharmacies.filter(({ id }) => id !== itemId);
    const stringified = JSON.stringify(updatedPharmacies);

    this.setState({ pharmacies: updatedPharmacies });
    saveFieldValue(stringified);
    updateFieldValue(stringified);
  };

  handleSelect = (suggestion: Suggestion<api.Places.PlaceSuggestion>) => {
    const { data } = suggestion;
    if (!data) return;

    this.selectPharmacy(data);
  };

  getPharmacySuggestions = async (
    input: string,
  ): Promise<Suggestion<api.Places.PlaceSuggestion>[]> => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return [];

    const pharmacySuggestionsArgs: {
      input: string;
      kind: api.Places.Kind;
      latitude?: number | null;
      longitude?: number | null;
    } = {
      input: trimmedInput,
      kind: api.Places.Kind.Pharmacy,
    };
    if (this.props.latitude) {
      pharmacySuggestionsArgs.latitude = this.props.latitude;
    }
    if (this.props.longitude) {
      pharmacySuggestionsArgs.longitude = this.props.longitude;
    }
    const suggestions = await api.client.Places.findTextSearchSuggestions(
      pharmacySuggestionsArgs,
    );
    return suggestions.map((suggestion) => {
      return {
        primaryLabel: suggestion.mainText,
        secondaryLabel: suggestion.secondaryText,
        icon: <LocationOnIcon />,
        data: suggestion,
      };
    });
  };

  render = () => {
    const { t } = this.props;
    const { pharmacies } = this.state;

    if (pharmacies.length) {
      return (
        <div>
          <ItemsList
            className={styles.itemsList}
            compact={false}
            closeIconTitle={t('Remove pharmacy')}
            items={pharmacies.map((pharmacy) => {
              const { staticMapUrl, id, name } = pharmacy;
              const staticMap = staticMapUrl && (
                <div className={styles.staticMapDiv}>
                  <img
                    className={styles.staticMap}
                    src={pharmacy.staticMapUrl}
                    alt={'Map of your pharmacy'}
                  />
                </div>
              );
              return {
                id,
                primaryLabel: name,
                secondaryLabel: Utils.getPrettyAddress(pharmacy),
                canRemove: true,
                ...(staticMap ? { children: staticMap } : {}),
              };
            })}
            primaryLabelSize={Size.Small}
            secondaryLabelSize={Size.Small}
            deleteItem={this.removePharmacy}
          />
        </div>
      );
    }

    return (
      <AutocompleteAsyncInput
        onSelect={(selected: Suggestion<api.Places.PlaceSuggestion>) =>
          this.handleSelect(selected)
        }
        getSuggestions={this.getPharmacySuggestions}
        placeholder={t(SEARCH_PLACEHOLDER)}
        icon={<SearchIcon className={styles.searchIcon} />}
        limit={MAX_PHARMACIES}
        initialString={this.state.currentText}
        searchText={t('Searching...')}
        emptyLabel={t('No matches found.')}
        promptText={t('Type to search...')}
        debounce={1000}
        minLength={MINIMUM_CHARACTERS_TO_SEARCH}
      />
    );
  };
}

export default withTranslation()(PharmacyComponent);
